/**
 * Maps item-policy evaluation rows for a single traveller context to legacy OfferCredit shape for LuxKit pills.
 * For multi-traveller tiles, pass the row for the primary traveller or aggregate in the caller.
 */
export function mapItemPolicyRowToOfferCredit(
  row: App.BusinessTraveller.ItemPolicyEvaluationRow | undefined,
): App.BusinessTraveller.OfferCredit | undefined {
  if (!row) {
    return undefined
  }

  if (row.budgetCompliance) {
    if (row.budgetCompliance === 'under_budget') {
      return {
        budgetCompliance: 'under_budget',
        creditValue: row.creditValue,
        budgetComparison: row.budgetComparisonPercent ?? 0,
      }
    }
    if (row.budgetCompliance === 'no_budget') {
      return {
        budgetCompliance: 'no_budget',
        creditValue: 0,
        budgetComparison: row.budgetComparisonPercent ?? 0,
      }
    }
    if (row.budgetCompliance === 'met_budget') {
      return {
        budgetCompliance: 'met_budget',
        creditValue: 0,
        budgetComparison: row.budgetComparisonPercent ?? 0,
      }
    }
    return {
      budgetCompliance: 'over_budget',
      creditValue: 0,
      budgetComparison: row.budgetComparisonPercent ?? 0,
    }
  }

  if (row.verdict === 'NO_POLICY') {
    return {
      budgetCompliance: 'no_budget',
      creditValue: 0,
      budgetComparison: row.budgetComparisonPercent ?? 0,
    }
  }

  if (row.verdict === 'IN_POLICY') {
    const budgetComparison = row.budgetComparisonPercent ?? 0

    if (row.creditValue > 0) {
      return {
        budgetCompliance: 'under_budget',
        creditValue: row.creditValue,
        budgetComparison,
      }
    }

    // Legacy credits return under_budget when under cap but credit is 0 (e.g. multi-room).
    if (budgetComparison > 0) {
      return {
        budgetCompliance: 'under_budget',
        creditValue: 0,
        budgetComparison,
      }
    }

    return {
      budgetCompliance: 'met_budget',
      creditValue: 0,
      budgetComparison,
    }
  }

  return {
    budgetCompliance: 'over_budget',
    creditValue: 0,
    budgetComparison: row.budgetComparisonPercent ?? 0,
  }
}

/** Picks the worst verdict across employees for a tile (over → no-policy mix → best credit among in-policy-only). */
export function mapItemPolicyRowsToAggregateOfferCredit(
  rows: Array<App.BusinessTraveller.ItemPolicyEvaluationRow> | undefined,
): App.BusinessTraveller.OfferCredit | undefined {
  if (!rows?.length) {
    return undefined
  }

  const outOfPolicy = rows.find(row => row.verdict === 'OUT_OF_POLICY')
  if (outOfPolicy) {
    return mapItemPolicyRowToOfferCredit(outOfPolicy)
  }

  const noPolicyRows = rows.filter(row => row.verdict === 'NO_POLICY')
  const inPolicyRows = rows.filter(row => row.verdict === 'IN_POLICY')

  if (noPolicyRows.length > 0 && inPolicyRows.length > 0) {
    return mapItemPolicyRowToOfferCredit(noPolicyRows[0])
  }

  if (noPolicyRows.length === rows.length) {
    return mapItemPolicyRowToOfferCredit(rows[0])
  }

  if (inPolicyRows.length === 0) {
    return mapItemPolicyRowToOfferCredit(rows[0])
  }

  const maxCredit = inPolicyRows.reduce((best, row) =>
    row.creditValue > (best?.creditValue ?? 0) ? row : best,
  inPolicyRows[0])

  return mapItemPolicyRowToOfferCredit(maxCredit)
}

/** UI state for credit pills: always OfferCredit on success, never raw rows-only result. */
export function mergeItemPolicyCalculatedCreditsWithOfferCredit(
  mappedOfferCredit: App.BusinessTraveller.OfferCredit | undefined,
  itemPolicyData: App.BusinessTraveller.StatefulData<App.BusinessTraveller.ItemPolicyCalculatedCreditsResult> | undefined,
): App.BusinessTraveller.StatefulData<App.BusinessTraveller.OfferCredit> | undefined {
  const fromRows =
    mappedOfferCredit ??
    (itemPolicyData?.status === 'success'
      ? mapItemPolicyRowsToAggregateOfferCredit(itemPolicyData.rowsByEmployee)
      : undefined)

  if (fromRows) {
    return { status: 'success', ...fromRows }
  }

  if (!itemPolicyData) {
    return undefined
  }

  if (itemPolicyData.status === 'pending') {
    return { status: 'pending' }
  }

  if (itemPolicyData.status === 'failed') {
    return { status: 'failed', error: itemPolicyData.error }
  }

  return {
    status: 'success',
    budgetCompliance: 'no_budget',
    creditValue: 0,
    budgetComparison: 0,
  }
}
